import butter from './butterClient'
import { shuffleArray } from '@centered/helpers'
import { BlogPost } from '../components/blog/BlogTypes'

export const BLOG_POSTS_TO_SHOW_PER_PAGE = 18

const listOptions: {
  exclude_body: boolean
  page_size: number
  order: string
  status?: string
} = {
  exclude_body: true,
  page_size: BLOG_POSTS_TO_SHOW_PER_PAGE,
  order: '-published', // Sort by published date in descending order
  status: 'published' // Explicitly request published posts
}

type PageMeta = {
  count: number
  next_page: number | null
  previous_page: number | null
}

type ListResults = {
  meta: PageMeta
  data: BlogPost[]
}

// Fetch all posts for static rendering
export async function getAllPostsWithSlug(): Promise<BlogPost[]> {
  const response = await butter.post.list({ ...listOptions, page_size: 100 })
  let results: ListResults = response.data
  let allPosts: BlogPost[] = results.data

  while (results.meta.next_page) {
    const response = await butter.post.list({
      ...listOptions,
      page: results.meta.next_page,
    })
    results = response.data
    allPosts = allPosts.concat(results.data)
  }

  return allPosts
}

const FEATURED_ARTICLES_CATEGORY_SLUG = 'FEATURED'

export async function getFeaturedPosts(): Promise<BlogPost[]> {
  const response = await butter.post.list({
    ...listOptions,
    page_size: 3,
    category_slug: FEATURED_ARTICLES_CATEGORY_SLUG,
  })
  return response.data?.data
}

// Categories to exclude from the blog page
const EXCLUDED_CATEGORIES = ['news', 'helpful-resources']

// Fetch posts for a specific category
export async function getPostsForPage(
  pageNumber = 1,
  amountToFetch = BLOG_POSTS_TO_SHOW_PER_PAGE,
  categorySlug?: string // Single category slug
): Promise<{
  meta: PageMeta
  data: BlogPost[]
}> {
  try {
    console.log('=== getPostsForPage called with ===')
    console.log('pageNumber:', pageNumber)
    console.log('amountToFetch:', amountToFetch)
    console.log('categorySlug:', categorySlug)

    // If fetching a specific category, use the original pagination approach
    if (categorySlug) {
      const response = await butter.post.list({
        ...listOptions,
        page: pageNumber,
        page_size: amountToFetch,
        category_slug: categorySlug
      })
      return response.data
    }

    // For the main blog page, fetch all posts first
    console.log('Fetching all posts...')
    const response = await butter.post.list({
      ...listOptions,
      page: 1,
      page_size: 100 // Maximum page size
    })

    let allPosts = response.data.data
    let nextPage = response.data.meta.next_page

    // Keep fetching until we get all posts
    while (nextPage) {
      console.log(`Fetching page ${nextPage}...`)
      const nextResponse = await butter.post.list({
        ...listOptions,
        page: nextPage,
        page_size: 100
      })
      allPosts = allPosts.concat(nextResponse.data.data)
      nextPage = nextResponse.data.meta.next_page
    }

    console.log(`Total posts fetched: ${allPosts.length}`)

    // Filter out excluded categories
    const filteredPosts = allPosts.filter((post: BlogPost) => 
      !post.categories.some(category => EXCLUDED_CATEGORIES.includes(category.slug))
    )

    console.log(`Posts after filtering: ${filteredPosts.length}`)

    // Sort by published date (newest first)
    filteredPosts.sort((a: BlogPost, b: BlogPost) => 
      new Date(b.published || '').getTime() - new Date(a.published || '').getTime()
    )

    // Calculate pagination
    const start = (pageNumber - 1) * amountToFetch
    const paginatedPosts = filteredPosts.slice(start, start + amountToFetch)

    return {
      meta: {
        count: filteredPosts.length,
        next_page: start + amountToFetch < filteredPosts.length ? pageNumber + 1 : null,
        previous_page: pageNumber > 1 ? pageNumber - 1 : null,
      },
      data: paginatedPosts,
    }
  } catch (e) {
    console.error('error in getPostsForPage', e)
    return Promise.reject(e)
  }
}

export async function getPostAndRelatedPosts(
  slug: string
): Promise<{ post: BlogPost; relatedPosts: BlogPost[] }> {
  const response = await butter.post.retrieve(slug)

  const post: BlogPost = response.data.data

  const relatedPostsResponse = await butter.post.list({
    category_slugs: post.categories.map((c) => c.slug),
    page_size: 20,
    exclude_body: true,
  })

  const unshuffledRelatedPosts: BlogPost[] = relatedPostsResponse.data.data
  const shuffledPosts = shuffleArray(unshuffledRelatedPosts)

  const relatedPosts = shuffledPosts
    .filter((p) => p && p.slug !== slug)
    .slice(0, 3)

  return {
    post,
    relatedPosts,
  }
}

export async function getPostsForFaqPage(): Promise<BlogPost[]> {
  try {
    const response = await butter.post.list({
      exclude_body: true,
      page_size: 3,
      category_slug: 'featured-on-faq',
    })

    return response.data.data
  } catch (e) {
    console.error('error in getPostsForFaqPage', e)
    return Promise.reject(e)
  }
}

export function calculateTotalNumberOfPages(totalItems: number): number {
  return Math.ceil(totalItems / BLOG_POSTS_TO_SHOW_PER_PAGE)
}

// **Restored Function: cropImageFromCdn**
export function cropImageFromCdn(
  url: string | undefined,
  width: number,
  height: number
): string {
  if (!url) {
    return ''
  }

  if (!url.includes('https://cdn.buttercms.com')) {
    console.warn('Requested crop on a non-buttercms url', url)
    return url
  }

  const imageId = url.replace('https://cdn.buttercms.com/', '')

  return `https://cdn.buttercms.com/resize=width:${width * 2},height:${
    height * 2
  },fit:crop/${imageId}`
}
